import React, { useState, useContext, useMemo } from "react";
import { RouteComponentProps, StaticContext } from "react-router";
import { Box, Typography } from "@material-ui/core";
import { B2BLoadingPopup, useDeviceTypes, Header } from "halifax";
import clsx from "clsx";

import "./styles.scss";
import { RegionType, TripCategory } from "redmond";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { FlightShopStep } from "./reducer";
import {
  FlightList,
  FlightShopHeader,
  FlightShopReviewItinerary,
  FlightShopProgressHeader,
  MobilePricePredictionSection,
  MobileFareDetails,
} from "./components";
import { FlightShopConnectorProps } from "./container";
import { FlightShopSearchControl } from "../search/components/FlightShopSearchControl";
import { FlightShopSearchFilter } from "../search/components/FlightShopSearchControl/components";
import { SortOptionSelection } from "./components/FlightShopHeader/components/SortOptionSelection";
import { AppliedFilterTags } from "../search/components/FlightShopSearchControl/components/AppliedFilterTags";
import { FareClassOptionSelection } from "./components/FlightShopHeader/components/FareClassOptionSelection";
import { RewardsAccountSelection } from "../rewards/components";
import {
  SEARCHING_FOR_FLIGHTS,
  SEARCHING_FOR_FLIGHTS_SECONDARY_MESSAGE,
  CHOOSING_FLIGHT_TEXT,
  FLIGHT_PRICES_TEXT,
  REVIEW_ITINERARY_TITLE_TEXT,
  REVIEW_ITINERARY_SUBTITLE_TEXT,
} from "./constants";
import { ClientContext } from "../../App";
import {
  PATH_HOME,
  PATH_SHOP,
  PATH_SHOP_MULTICITY,
} from "../../utils/urlPaths";
import {
  pushToPathWithExistingQueryParams,
  parseQueryString,
  IFlightShopParsedQuery,
} from "./utils/parseQueryString";
import {
  AVAILABLE,
  XMOD_V1_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../context/experiments";
import {
  CHOOSE_DEPARTURE_TITLE,
  CHOOSE_RETURN_TITLE,
  PORTAL_TITLE,
  REVIEW_ITINERARY_TITLE,
} from "../../lang/textConstants";

export interface IFlightShopProps
  extends FlightShopConnectorProps,
    RouteComponentProps<{}, StaticContext, { prevPath?: string }> {}

export interface IFlightShopProps extends FlightShopConnectorProps {}

export const FlightShop = (props: IFlightShopProps) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const {
    currentStep,
    tripSummariesLoading,
    history,
    fetchTripSummaries,
    fetchTripSummariesForPrediction,
    setFlightShopProgress,
    tripSummaries,
    refreshPrediction,
    origin,
    destination,
    tripCategory,
    flightShopType,
    sortOption,
    setSortOption,
    airports,
  } = props;
  const { matchesMobile, matchesDesktop, matchesLargeDesktop } =
    useDeviceTypes();
  const matchesMediumDesktopOnly = matchesDesktop && !matchesLargeDesktop;
  const clientContext = useContext(ClientContext);

  const { experiments } = useExperiments();

  const isInReviewStep = currentStep === FlightShopStep.ReviewItinerary;
  const isInFareDetailsStep = currentStep === FlightShopStep.FareDetails;
  const isInPredictionStep = currentStep === FlightShopStep.PricePrediction;
  const isInChooseDepartureStep =
    currentStep === FlightShopStep.ChooseDeparture;
  const isInChooseReturnStep = currentStep === FlightShopStep.ChooseReturn;
  const [isTripSummariesEmpty, setIsTripSummariesEmpty] = useState(
    Object.keys(tripSummaries).length === 0
  );

  const isXmodRedesign =
    getExperimentVariant(experiments, XMOD_V1_EXPERIMENT) === AVAILABLE;

  React.useEffect(() => {
    setIsTripSummariesEmpty(Object.keys(tripSummaries).length === 0);
  }, [tripSummaries]);

  React.useEffect(() => {
    const prevPath = history.location.state?.prevPath;
    if (
      isTripSummariesEmpty ||
      (!prevPath && !isInReviewStep && !isInFareDetailsStep)
    ) {
      fetchTripSummaries(history, matchesMobile);
    }
  }, []);

  React.useEffect(() => {
    if (refreshPrediction && currentStep === FlightShopStep.ChooseDeparture) {
      fetchTripSummariesForPrediction(history, false);
    }
  }, [refreshPrediction, flightShopType]);

  // Update state depending on query param
  React.useEffect(() => {
    const queryString = parseQueryString(history) as IFlightShopParsedQuery;

    if (queryString.flightShopProgress) {
      setFlightShopProgress(queryString.flightShopProgress);
    }
  }, [history.location.search]);

  const shopPath = useMemo(
    () =>
      tripCategory === TripCategory.MULTI_CITY
        ? PATH_SHOP_MULTICITY
        : PATH_SHOP,
    [tripCategory]
  );

  // Update query sting if flightShopProgress does not match state
  React.useEffect(() => {
    const queryString = parseQueryString(history) as IFlightShopParsedQuery;
    if (queryString.flightShopProgress !== currentStep) {
      pushToPathWithExistingQueryParams(
        history,
        shopPath,
        {
          flightShopProgress: currentStep,
        },
        false
      );
    }
    switch (currentStep) {
      case FlightShopStep.ChooseDeparture:
        document.title = CHOOSE_DEPARTURE_TITLE;
        break;
      case FlightShopStep.ChooseReturn:
        document.title = CHOOSE_RETURN_TITLE;
        break;
      case FlightShopStep.ReviewItinerary:
        document.title = REVIEW_ITINERARY_TITLE;
        break;
    }
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, [currentStep]);

  // note: when currentStep === PricePrediction, push to ChooseDeparture
  React.useEffect(() => {
    if (currentStep === FlightShopStep.PricePrediction) {
      // Needs to replace the query param and not push to history.
      pushToPathWithExistingQueryParams(
        history,
        shopPath,
        {
          flightShopProgress: FlightShopStep.ChooseDeparture,
        },
        true
      );
      setFlightShopProgress(FlightShopStep.ChooseDeparture);
    }
  }, [currentStep, matchesMobile]);

  // Scroll the screen to the top when the progress changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleOnCompleteFareSelect = () => {
    // note: the review itinerary step only exists in the default flight shop funnel
    setFlightShopProgress(FlightShopStep.ReviewItinerary);
  };

  const renderDesktopFlightShopRewardsHeader = () => (
    <Header
      className="desktop-flight-shop-rewards-header-container"
      left={
        <Box className="rewards-account-section-left-content">
          <Box className="logo" onClick={() => history.push(PATH_HOME)}>
            {clientContext.logo}
          </Box>
          <Box className="rewards-account-section-travel-details">
            <Typography variant="body1">
              {isInReviewStep
                ? REVIEW_ITINERARY_TITLE_TEXT
                : origin && destination
                ? CHOOSING_FLIGHT_TEXT(
                    currentStep,
                    origin.label,
                    destination.label
                  )
                : ""}
            </Typography>
            <Typography variant="body2">
              {isInReviewStep
                ? REVIEW_ITINERARY_SUBTITLE_TEXT
                : FLIGHT_PRICES_TEXT(tripCategory)}
            </Typography>
          </Box>
        </Box>
      }
      right={
        <Box className="desktop-flight-shop-rewards-account-contents">
          <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
        </Box>
      }
    />
  );

  const renderDesktopFlightShop = () => (
    <>
      {renderDesktopFlightShopRewardsHeader()}
      <Box className="flight-shop-result-container">
        {!isInReviewStep && <FlightShopSearchControl disabled={false} />}
        {!isTripSummariesEmpty && (
          <FlightShopHeader
            isMobile={false}
            airports={airports}
            isMediumDesktop={matchesMediumDesktopOnly}
            history={history}
          />
        )}
        {!isInReviewStep && (
          <FlightList onCompleteFareSelect={handleOnCompleteFareSelect} />
        )}
        {isInReviewStep && (
          <FlightShopReviewItinerary isMobile={false} useDetailsPrefix />
        )}
      </Box>
    </>
  );

  const renderMobileFlightShop = () => (
    <>
      <Box className="progress-header-menu-wrapper">
        <FlightShopProgressHeader
          onFiltersClick={() => setFiltersOpen(true)}
          className={
            isInPredictionStep
              ? "mobile-prediction-header"
              : "mobile-shop-header"
          }
          isEditHidden={false}
          allFiltersModalOpen={filtersOpen}
          setAllFiltersModalOpen={setFiltersOpen}
        />
      </Box>
      {(isInChooseDepartureStep || isInChooseReturnStep) && (
        <>
          <Box className="mobile-flight-shop-search-filters-and-sort-section">
            <FlightShopSearchFilter
              hideAirportFilter={
                origin?.id.code.regionType === RegionType.Airport
              }
            />
            <SortOptionSelection
              sortOption={sortOption}
              setSortOption={setSortOption}
            />
          </Box>
          {isInChooseDepartureStep && (
            <Box>
              <MobilePricePredictionSection
                filtersOpen={filtersOpen}
                setFiltersOpen={setFiltersOpen}
                history={history}
              />
            </Box>
          )}
          <Box className="mobile-filter-reset-chips">
            <AppliedFilterTags />
          </Box>
          <FareClassOptionSelection />
          {!isTripSummariesEmpty && (
            <FlightShopHeader isMobile airports={airports} history={history} />
          )}
          <FlightList onCompleteFareSelect={handleOnCompleteFareSelect} />
        </>
      )}
      {isInReviewStep && (
        <>
          <FlightShopHeader isMobile airports={airports} history={history} />
          <FlightShopReviewItinerary isMobile useDetailsPrefix />
        </>
      )}
      {isInFareDetailsStep && (
        <MobileFareDetails hasActiveRefundableFare={false} />
      )}
    </>
  );

  return (
    <>
      <Box className={clsx("flight-shop-root", { mobile: matchesMobile })}>
        <Box className="flight-shop-container">
          {matchesDesktop && renderDesktopFlightShop()}
          {matchesMobile && renderMobileFlightShop()}
          {tripSummariesLoading && (
            <B2BLoadingPopup
              open={tripSummariesLoading}
              message={SEARCHING_FOR_FLIGHTS}
              secondaryMessage={SEARCHING_FOR_FLIGHTS_SECONDARY_MESSAGE}
              image={clientContext.searchImage ?? ""}
              className="flight-search-loading-popup"
              popupSize={matchesMobile ? "mobile" : "desktop"}
              onClose={() => {
                travelAppInterface.sendEvent({
                  name: TravelAppInterfaceEventName.SEARCH_LOADED,
                  funnel: TravelAppInterfaceFunnelType.FLIGHTS,
                });
              }}
              isXmod={isXmodRedesign}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
